/**
 * UpgradeModal Component
 *
 * Modal for upgrading to Premium subscription.
 * Integrates PayPal payments for both one-time (monthly) and recurring (annual) options.
 *
 * @module components/subscription/UpgradeModal
 */

import { useState, useEffect, useCallback } from 'react';
import { usePayPalScript } from '../../hooks/usePayPalScript';
import { useAuthStore } from '../../store/authStore';
import { PayPalButton, PayPalOrderData } from './PayPalButton';
import { PayPalSubscriptionButton, PayPalSubscriptionData } from './PayPalSubscriptionButton';
import { TermsAcceptance } from './TermsAcceptance';
import { PaymentTypeSelector, PaymentType } from './PaymentTypeSelector';
import {
  upgradeWithPayPal,
  activateSubscription,
} from '../../services/subscription.service';

/**
 * Props for the UpgradeModal component
 */
export interface UpgradeModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when upgrade is successful */
  onSuccess: () => void;
  /** Monthly price in EUR (from parent) */
  monthlyPrice: number;
  /** Annual price per month in EUR (from parent) */
  annualMonthlyPrice: number;
  /** Annual total price in EUR (from parent) */
  annualTotalPrice: number;
  /** PayPal Client ID (from parent) */
  paypalClientId: string;
  /** PayPal Plan ID for subscriptions (from parent) */
  paypalPlanId: string;
  /** Initial payment type selection (from parent) */
  initialPaymentType?: PaymentType;
}

/**
 * UpgradeModal Component
 *
 * Full-featured modal for Premium subscription upgrade.
 * Handles payment type selection, terms acceptance, and PayPal integration.
 */
export function UpgradeModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  monthlyPrice,
  annualMonthlyPrice,
  annualTotalPrice,
  paypalClientId,
  paypalPlanId,
  initialPaymentType = 'annual',
}: UpgradeModalProps) {
  // Store state
  const { user, refreshUser } = useAuthStore();

  // Local state
  const [paymentType, setPaymentType] = useState<PaymentType>(initialPaymentType);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // PayPal SDK
  const { isLoaded: paypalLoaded, isLoading: paypalLoading, error: paypalError } = usePayPalScript(paypalClientId);

  // Reset state when modal opens, but keep the initial payment type from parent
  useEffect(() => {
    if (isOpen) {
      setPaymentType(initialPaymentType);
      setTermsAccepted(false);
      setIsProcessing(false);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, initialPaymentType]);

  /**
   * Handle successful one-time PayPal payment
   */
  const handlePayPalSuccess = useCallback(
    async (orderData: PayPalOrderData) => {
      setIsProcessing(true);
      setError(null);

      try {
        const result = await upgradeWithPayPal(
          orderData.orderId,
          orderData.captureId,
          orderData.amount,
          orderData.payerEmail
        );

        if (result.success) {
          setSuccess(true);
          // Refresh user data to get updated subscription status
          await refreshUser();
          // Notify parent of success after a brief delay
          setTimeout(() => {
            onSuccess();
          }, 2000);
        } else {
          setError(result.message || 'Erreur lors de l\'activation de l\'abonnement');
        }
      } catch (err) {
        console.error('PayPal upgrade error:', err);
        setError('Erreur lors de l\'activation de l\'abonnement. Veuillez réessayer.');
      } finally {
        setIsProcessing(false);
      }
    },
    [refreshUser, onSuccess]
  );

  /**
   * Handle successful PayPal subscription
   */
  const handleSubscriptionSuccess = useCallback(
    async (subscriptionData: PayPalSubscriptionData) => {
      setIsProcessing(true);
      setError(null);

      try {
        const result = await activateSubscription(
          subscriptionData.subscriptionId,
          subscriptionData.planId,
          subscriptionData.payerEmail
        );

        if (result.success) {
          setSuccess(true);
          // Refresh user data to get updated subscription status
          await refreshUser();
          // Notify parent of success after a brief delay
          setTimeout(() => {
            onSuccess();
          }, 2000);
        } else {
          setError(result.message || 'Erreur lors de l\'activation de l\'abonnement');
        }
      } catch (err) {
        console.error('PayPal subscription error:', err);
        setError('Erreur lors de l\'activation de l\'abonnement. Veuillez réessayer.');
      } finally {
        setIsProcessing(false);
      }
    },
    [refreshUser, onSuccess]
  );

  /**
   * Handle PayPal cancellation
   */
  const handleCancel = useCallback(() => {
    setError('Paiement annulé');
  }, []);

  /**
   * Handle PayPal error
   */
  const handlePayPalError = useCallback((err: Error) => {
    console.error('PayPal error:', err);
    setError('Erreur PayPal. Veuillez réessayer.');
  }, []);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Check if PayPal is configured
  const isPayPalConfigured = paypalClientId && (paymentType === 'monthly' || paypalPlanId);
  const canShowPayPal = termsAccepted && paypalLoaded && isPayPalConfigured && !isProcessing && !success;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Passer à Premium
            </h2>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              aria-label="Fermer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Débloquez toutes les fonctionnalités de Budgira
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">
                    Paiement réussi !
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Votre abonnement Premium est maintenant actif.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !success && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {/* Premium Features */}
          {!success && (
            <>
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Avantages Premium
                </h3>
                <ul className="space-y-2">
                  {[
                    'Opérations illimitées',
                    'Comptes bancaires illimités',
                    'Export des données',
                    'Statistiques avancées',
                    'Support prioritaire',
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <svg className="w-5 h-5 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Payment Type Selector */}
              <PaymentTypeSelector
                selectedType={paymentType}
                onChange={setPaymentType}
                monthlyPrice={monthlyPrice}
                annualPricePerMonth={annualMonthlyPrice}
                annualTotalPrice={annualTotalPrice}
                disabled={isProcessing}
              />

              {/* Terms Acceptance */}
              <TermsAcceptance
                accepted={termsAccepted}
                onChange={setTermsAccepted}
                disabled={isProcessing}
              />

              {/* PayPal Loading State */}
              {paypalLoading && (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">
                    Chargement de PayPal...
                  </span>
                </div>
              )}

              {/* PayPal Error from SDK */}
              {paypalError && paypalClientId && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-yellow-800 dark:text-yellow-200">
                    {paypalError.message}
                  </p>
                </div>
              )}

              {/* PayPal Client ID Not Configured */}
              {!paypalClientId && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-yellow-800 dark:text-yellow-200">
                    Le paiement PayPal n'est pas configuré. Veuillez contacter l'administrateur.
                  </p>
                </div>
              )}

              {/* PayPal Plan Not Configured (for annual) - show only when annual is selected */}
              {paymentType === 'annual' && paypalClientId && !paypalPlanId && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-yellow-800 dark:text-yellow-200">
                    L'abonnement annuel n'est pas configuré. Veuillez choisir le paiement mensuel ou contacter l'administrateur.
                  </p>
                </div>
              )}

              {/* Processing State */}
              {isProcessing && (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">
                    Traitement du paiement...
                  </span>
                </div>
              )}

              {/* PayPal Buttons */}
              {paymentType === 'monthly' && canShowPayPal && user && (
                <PayPalButton
                  userId={user.id}
                  price={monthlyPrice}
                  onSuccess={handlePayPalSuccess}
                  onCancel={handleCancel}
                  onError={handlePayPalError}
                  enabled={true}
                />
              )}

              {paymentType === 'annual' && canShowPayPal && paypalPlanId && (
                <PayPalSubscriptionButton
                  planId={paypalPlanId}
                  onSuccess={handleSubscriptionSuccess}
                  onCancel={handleCancel}
                  onError={handlePayPalError}
                  enabled={true}
                />
              )}

              {/* Terms reminder */}
              {!termsAccepted && !isProcessing && paypalLoaded && isPayPalConfigured && (
                <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                  Veuillez accepter les conditions pour continuer
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Paiement sécurisé par PayPal
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpgradeModal;
